import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/navigation/AdminNavigation';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { categoryService } from '../../services/categoryService';

const CategoryManagement = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: 'Folder',
    displayOrder: 0,
    isActive: true
  });

  const iconOptions = [
    'Folder', 'Film', 'Tv', 'Trophy', 'Smartphone', 'Music', 'Camera', 'Video', 'Image', 'Star'
  ];

  useEffect(() => {
    loadCategories();

    const unsubscribe = categoryService?.subscribeToChanges((payload) => {
      if (payload?.eventType === 'INSERT') {
        setCategories(prev => [...prev, payload?.new]);
      } else if (payload?.eventType === 'UPDATE') {
        setCategories(prev => prev?.map(cat => 
          cat?.id === payload?.new?.id ? payload?.new : cat
        ));
      } else if (payload?.eventType === 'DELETE') {
        setCategories(prev => prev?.filter(cat => cat?.id !== payload?.old?.id));
      }
    });

    return () => unsubscribe();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await categoryService?.getAll(true);
      setCategories(data);
    } catch (err) {
      setError(err?.message || 'Erro ao carregar categorias');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category?.name,
        slug: category?.slug,
        description: category?.description || '',
        icon: category?.icon || 'Folder',
        displayOrder: category?.displayOrder || 0,
        isActive: category?.isActive
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        slug: '',
        description: '',
        icon: 'Folder',
        displayOrder: categories?.length || 0,
        isActive: true
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      icon: 'Folder',
      displayOrder: 0,
      isActive: true
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    try {
      if (editingCategory) {
        await categoryService?.update(editingCategory?.id, formData);
      } else {
        await categoryService?.create(formData);
      }
      handleCloseModal();
      loadCategories();
    } catch (err) {
      setError(err?.message || 'Erro ao salvar categoria');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta categoria?')) return;
    
    try {
      await categoryService?.delete(id);
      loadCategories();
    } catch (err) {
      setError(err?.message || 'Erro ao excluir categoria');
    }
  };

  const generateSlug = (name) => {
    return name
      ?.toLowerCase()
      ?.normalize('NFD')
      ?.replace(/[\u0300-\u036f]/g, '')
      ?.replace(/[^a-z0-9]+/g, '-')
      ?.replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (value) => {
    setFormData(prev => ({
      ...prev,
      name: value,
      slug: generateSlug(value)
    }));
  };

  return (
    <>
      <Helmet>
        <title>Gerenciar Categorias - UP MIDIA Admin</title>
        <meta name="description" content="Gerencie categorias de conteúdo do sistema UP MIDIA" />
      </Helmet>
      <div className="min-h-screen bg-background pb-20">
        <div className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="container mx-auto px-4 py-4">
            <AdminNavigation />
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Gerenciar Categorias
              </h1>
              <p className="text-muted-foreground">
                Crie, edite e organize as categorias de conteúdo
              </p>
            </div>
            <Button
              onClick={() => handleOpenModal()}
              iconName="Plus"
              className="glow-primary"
            >
              Nova Categoria
            </Button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-500">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin glow-primary"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories?.map((category) => (
                <div
                  key={category?.id}
                  className="bg-card rounded-lg p-6 glow-primary hover:glow-primary-hover transition-smooth"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center glow-primary">
                        <Icon name={category?.icon} size={24} color="var(--color-primary)" />
                      </div>
                      <div>
                        <h3 className="text-lg font-heading font-bold text-foreground">
                          {category?.name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {category?.slug}
                        </p>
                      </div>
                    </div>
                    <div className={`px-2 py-1 rounded text-xs font-medium ${
                      category?.isActive 
                        ? 'bg-green-500/10 text-green-500' :'bg-red-500/10 text-red-500'
                    }`}>
                      {category?.isActive ? 'Ativa' : 'Inativa'}
                    </div>
                  </div>

                  {category?.description && (
                    <p className="text-sm text-muted-foreground mb-4">
                      {category?.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <span className="text-sm text-muted-foreground">
                      Ordem: {category?.displayOrder}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => handleOpenModal(category)}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => handleDelete(category?.id)}
                        className="text-red-500 hover:text-red-600"
                      >
                        Excluir
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-card rounded-lg p-6 w-full max-w-2xl glow-primary">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-heading font-bold text-foreground">
                  {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="p-2 rounded-md hover:bg-muted transition-smooth"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Nome da Categoria
                  </label>
                  <Input
                    value={formData?.name}
                    onChange={(e) => handleNameChange(e?.target?.value)}
                    placeholder="Ex: Big Brother Brasil"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Slug (URL amigável)
                  </label>
                  <Input
                    value={formData?.slug}
                    onChange={(e) => setFormData(prev => ({ ...prev, slug: e?.target?.value }))}
                    placeholder="Ex: big-brother-brasil"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Descrição
                  </label>
                  <textarea
                    value={formData?.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
                    placeholder="Descrição da categoria"
                    className="w-full px-4 py-2 bg-muted border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Ícone
                    </label>
                    <select
                      value={formData?.icon}
                      onChange={(e) => setFormData(prev => ({ ...prev, icon: e?.target?.value }))}
                      className="w-full px-4 py-2 bg-muted border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {iconOptions?.map(icon => (
                        <option key={icon} value={icon}>{icon}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Ordem de Exibição
                    </label>
                    <Input
                      type="number"
                      value={formData?.displayOrder}
                      onChange={(e) => setFormData(prev => ({ ...prev, displayOrder: parseInt(e?.target?.value) || 0 }))}
                      min={0}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData?.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e?.target?.checked }))}
                    className="w-4 h-4 text-primary bg-muted border-border rounded focus:ring-primary"
                  />
                  <label htmlFor="isActive" className="text-sm font-medium text-foreground">
                    Categoria ativa
                  </label>
                </div>

                <div className="flex items-center justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleCloseModal}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" className="glow-primary">
                    {editingCategory ? 'Salvar Alterações' : 'Criar Categoria'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CategoryManagement;